import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { formatTime, formatCents } from '../lib/format';
import { SkeletonList } from '../components/Skeleton';

interface Booking {
  id: number;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string | null;
  service_name: string;
  price_cents: number;
  color: string | null;
  amount_collected_cents: number | null;
}

type View = 'week' | 'month';

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay()); // Sunday
  return out;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function Week() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get('view') === 'month' ? 'month' : 'week';

  const [anchor, setAnchor] = useState(() =>
    view === 'month' ? startOfMonth(new Date()) : startOfWeek(new Date())
  );
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // When view flips, snap anchor to the right kind of boundary
  useEffect(() => {
    setAnchor(view === 'month' ? startOfMonth(anchor) : startOfWeek(anchor));
  }, [view]);

  // For month view we render a 6-row grid that starts at the Sunday before the 1st
  const gridStart = useMemo(() => {
    return view === 'month' ? startOfWeek(anchor) : anchor;
  }, [view, anchor]);

  const gridDays = useMemo(() => {
    const count = view === 'month' ? 42 : 7;
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [view, gridStart]);

  useEffect(() => {
    setBookings(null);
    const from = ymd(gridDays[0]);
    const to = ymd(gridDays[gridDays.length - 1]);
    apiGet<{ bookings: Booking[] }>(`/api/bookings?from=${from}&to=${to}`)
      .then((r) => setBookings(r.bookings.filter((b) => b.status !== 'cancelled')))
      .catch((e) => setErr((e as Error).message));
  }, [view, anchor]);

  function setView(next: View) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'week') sp.delete('view');
    else sp.set('view', next);
    setSearchParams(sp, { replace: true });
  }

  function shift(units: number) {
    const d = new Date(anchor);
    if (view === 'month') d.setMonth(d.getMonth() + units);
    else d.setDate(d.getDate() + units * 7);
    setAnchor(d);
  }

  function jumpToToday() {
    setAnchor(view === 'month' ? startOfMonth(new Date()) : startOfWeek(new Date()));
  }

  const byDay = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    for (const b of bookings || []) {
      const key = b.start_at.split(' ')[0];
      (map[key] ||= []).push(b);
    }
    return map;
  }, [bookings]);

  const today = ymd(new Date());

  const rangeRevenue = useMemo(() => {
    if (!bookings) return { cents: 0, count: 0 };
    let cents = 0;
    let count = 0;
    for (const b of bookings) {
      if (b.status !== 'completed') continue;
      cents += b.amount_collected_cents ?? b.price_cents;
      count++;
    }
    return { cents, count };
  }, [bookings]);

  const rangeLabel =
    view === 'month'
      ? anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : `${gridDays[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${gridDays[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold">{view === 'month' ? 'Month' : 'Week'}</h1>
        <div className="flex items-center gap-1 bg-panel-2 rounded-xl p-1">
          <button
            onClick={() => setView('week')}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
              view === 'week' ? 'bg-accent/20 text-accent-2' : 'text-muted hover:text-text'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setView('month')}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
              view === 'month' ? 'bg-accent/20 text-accent-2' : 'text-muted hover:text-text'
            }`}
          >
            Month
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-muted text-sm">{rangeLabel}</p>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="btn-ghost px-3 py-2 text-sm">←</button>
          <button onClick={jumpToToday} className="btn-ghost px-3 py-2 text-sm">Today</button>
          <button onClick={() => shift(1)} className="btn-ghost px-3 py-2 text-sm">→</button>
        </div>
      </div>

      {bookings && rangeRevenue.count > 0 && (
        <div className="card flex items-center justify-between border-emerald-500/30 bg-emerald-500/5">
          <div>
            <div className="text-2xl font-mono font-bold tracking-tight">{formatCents(rangeRevenue.cents)}</div>
            <div className="text-xs text-muted">collected this {view}</div>
          </div>
          <div className="text-right">
            <div className="text-base font-mono font-semibold">{rangeRevenue.count}</div>
            <div className="text-xs text-muted">complete</div>
          </div>
        </div>
      )}

      {err && <p className="text-rose-400">{err}</p>}

      {!bookings ? (
        <SkeletonList count={view === 'month' ? 6 : 7} variant={view === 'month' ? 'card' : 'row'} />
      ) : view === 'week' ? (
        <WeekList days={gridDays} byDay={byDay} today={today} onPick={(k) => navigate(`/?date=${k}`)} />
      ) : (
        <MonthGrid
          days={gridDays}
          byDay={byDay}
          today={today}
          activeMonth={anchor.getMonth()}
          onPick={(k) => navigate(`/?date=${k}`)}
        />
      )}
    </div>
  );
}

function WeekList({
  days,
  byDay,
  today,
  onPick,
}: {
  days: Date[];
  byDay: Record<string, Booking[]>;
  today: string;
  onPick: (ymd: string) => void;
}) {
  return (
    <div className="space-y-2">
      {days.map((d) => {
        const key = ymd(d);
        const items = byDay[key] || [];
        const isToday = key === today;
        return (
          <div
            key={key}
            className={`card cursor-pointer ${isToday ? 'border-accent' : ''}`}
            onClick={() => onPick(key)}
          >
            <div className="flex items-center justify-between mb-1">
              <div>
                <span className="font-semibold">{DAY_NAMES[d.getDay()]}</span>
                <span className="text-muted text-sm ml-2">
                  {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
                {isToday && <span className="ml-2 pill bg-accent/20 text-accent-2">today</span>}
              </div>
              <span className="text-muted text-sm font-mono">{items.length} booking{items.length === 1 ? '' : 's'}</span>
            </div>

            {items.length > 0 ? (
              <div className="space-y-1">
                {items.slice(0, 4).map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center gap-2 text-sm border-l-2 pl-2"
                    style={{ borderLeftColor: b.color || 'var(--accent)' }}
                  >
                    <span className="w-16 text-muted font-mono">{formatTime(b.start_at)}</span>
                    <span className="flex-1 truncate">{b.customer_name || '—'}</span>
                    <span className="text-muted truncate hidden sm:inline">{b.service_name}</span>
                  </div>
                ))}
                {items.length > 4 && (
                  <div className="text-xs text-muted pl-2">+ {items.length - 4} more</div>
                )}
              </div>
            ) : (
              <p className="text-muted text-sm italic">No bookings</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MonthGrid({
  days,
  byDay,
  today,
  activeMonth,
  onPick,
}: {
  days: Date[];
  byDay: Record<string, Booking[]>;
  today: string;
  activeMonth: number;
  onPick: (ymd: string) => void;
}) {
  const MAX_VISIBLE = 3;
  return (
    <div className="card p-2">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_NAMES.map((n) => (
          <div key={n} className="text-center text-xs text-muted uppercase tracking-wider py-1">
            {n}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const key = ymd(d);
          const items = (byDay[key] || []).slice().sort((a, b) => a.start_at.localeCompare(b.start_at));
          const isToday = key === today;
          const inMonth = d.getMonth() === activeMonth;
          const visible = items.slice(0, MAX_VISIBLE);
          const overflow = items.length - visible.length;

          return (
            <button
              key={key}
              onClick={() => onPick(key)}
              className={`min-h-[96px] rounded-lg p-1 flex flex-col items-stretch text-left transition active:scale-95 overflow-hidden ${
                isToday
                  ? 'border-2 border-accent bg-accent/10'
                  : 'border border-border hover:bg-panel-2'
              } ${inMonth ? '' : 'opacity-40'}`}
            >
              <div className="flex items-baseline justify-between px-1 pb-1">
                <span className={`font-mono text-sm ${isToday ? 'text-accent-2 font-bold' : ''}`}>
                  {d.getDate()}
                </span>
                {items.length > MAX_VISIBLE && (
                  <span className="text-[10px] font-mono text-muted">{items.length}</span>
                )}
              </div>
              <div className="flex flex-col gap-0.5 flex-1">
                {visible.map((b) => (
                  <div
                    key={b.id}
                    className="rounded px-1 py-0.5 text-[10px] leading-tight border-l-2 truncate flex items-center gap-1"
                    style={{
                      borderLeftColor: b.color || 'var(--accent)',
                      backgroundColor: 'var(--panel-2)',
                      textDecoration: b.status === 'cancelled' || b.status === 'no_show' ? 'line-through' : 'none',
                      opacity: b.status === 'cancelled' || b.status === 'no_show' ? 0.5 : 1,
                    }}
                  >
                    <span className="font-mono text-muted shrink-0">{formatTime(b.start_at).replace(' ', '')}</span>
                    <span className="truncate">{b.customer_name || '—'}</span>
                  </div>
                ))}
                {overflow > 0 && (
                  <div className="text-[10px] text-muted px-1">+{overflow} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
